/**
 * Life4Earth V2 — Archetype Configuration
 *
 * Single source of truth for:
 *   - Which features are visible at each milestone stage
 *   - What milestone order looks like for each archetype
 *   - Default landing pages per archetype
 *   - Severity tiering (mandatory · recommended · optional) for the Tasks tab
 *   - Onboarding wizard step configuration
 *
 * Architecture ref: Onboarding Spec §3 (Archetype Definitions),
 *                   §5.2 (Milestone-Aligned Recommendations),
 *                   DEV-725 (Four-tier User Progression Model)
 *
 * ─────────────────────────────────────────────────────────────────────
 *  ARCHETYPE TAXONOMY (LOCKED 2026-05-07; amended 2026-07-08)
 * ─────────────────────────────────────────────────────────────────────
 * The four archetypes mirror the LeftNav tier groups exactly:
 *   explorer · contributor · leader · governor
 *
 * AMENDMENT 2026-07-08 (Skip ratification; localization ticket DEV-1431):
 * the third tier's DISPLAY name is now "Champion" (nav group + rung label
 * + archetype label). Rationale: "Lead/Leader" collides with the role
 * vocabulary (Mini-Project Lead, Project Lead, workspace team lead) and
 * the trust ladder must stay separate from roles. DISPLAY STRINGS ONLY —
 * the 'lead' tier key, the 'leader' slug below, LEGACY_SLUG_MAP, and every
 * code identifier stay frozen (same precedent as POLICY_ENABLER_VOCABULARY:
 * the pillar key stayed 'enabler' when its labels moved).
 *
 * Earlier drafts had seven archetypes (explorer, expert, youth, trainee,
 * project_manager, portfolio_observer, system_admin) but only `explorer`
 * and `expert` had full configs. The seven-name taxonomy added persona
 * granularity that the platform wasn't actively using and made the
 * archetype/tier mental model harder to reason about. Collapsing to
 * four creates a 1:1 mapping with the LeftNav groups (Explore /
 * Contribute / Lead / Govern). Persona-level distinction (e.g., "this
 * user is a youth-account explorer") can live in user metadata or
 * future role assignments rather than complicating archetype.
 *
 * Old → new slug migration is handled in `getArchetypeConfig()` so any
 * existing `user_onboarding_profiles` rows with old slugs still resolve
 * to a valid config — no DB migration required.
 *
 * ─────────────────────────────────────────────────────────────────────
 *  SEVERITY TIERING (DEV-681 Phase 3 — Tasks tab)
 * ─────────────────────────────────────────────────────────────────────
 * Each MilestoneUnlock carries a `severity`:
 *   - mandatory:   blocks tier advancement; user must complete to progress.
 *                  E.g., enable 2FA, complete profile, accept EULA.
 *   - recommended: strongly suggested; drives engagement quality but
 *                  doesn't strictly block advancement on its own.
 *                  E.g., follow first community, post first comment.
 *   - optional:    nice-to-have for users who want full breadth.
 *                  E.g., explore a community outside your home region.
 *
 * The Tasks tab in the Action Center sorts by severity (mandatory first)
 * within each archetype's progression. The Tasks tab badge counts
 * mandatory + recommended; optional doesn't ping the bell.
 */

// ── Milestone keys ───────────────────────────────────────────
//
// All canonical milestone identifiers — including ones whose underlying
// features haven't shipped yet. Keeping the keys reserved means that
// when those features land we can wire the milestone back into an
// archetype's progression with a one-line addition rather than
// re-introducing the slug under a new name.
//
// Currently-unbuilt milestones (NOT included in any progression):
//   - `first_follow`           — depends on DEV-540 (Follow/Unfollow)
//   - `first_comment`          — no comment-create code in codebase yet
//   - `first_cross_community`  — depends on follow (DEV-540)
//   - `first_role_completed`   — role completion lifecycle not built
//   - `earned_inviter_status`  — depends on DEV-672 (trust scoring)
//
// When any of these features ship: re-add the milestone to the
// appropriate archetype's `milestoneProgression` and wire a detector
// in `milestoneEvaluator.ts`.

export const MILESTONE_KEYS = [
  // Identity & Trust (Explorer→Contributor mandatory checklist per DEV-725)
  'enable_2fa',
  'complete_profile',
  'accept_eula',
  'first_login',
  // Chunk B slice D (§7.16) — beat-2 reveal seen-flag (migration 277):
  // the one-time ExplainRadarModal + first-visit reveal choreography.
  'radar_explained',
  // Platform Literacy (RESERVED — features pending; see header comment)
  'first_follow',
  'first_comment',
  'first_cross_community',
  // Contribution
  'first_role_completed', // RESERVED — role completion lifecycle pending
  // Leadership
  'first_project_created',
  'first_workspace_created',
  'earned_inviter_status', // RESERVED — depends on DEV-672 trust scoring
  // Acclimation flags (chunk A — §7.7/§7.9, migration 264). Profile-persisted
  // FLAGS riding the milestone machinery (server-side persistence per §7.9
  // r4), NOT archetype-progression milestones — none unlock features and none
  // appear in any milestoneProgression.
  'welcome_nudge_dismissed',
  'action_center_visited',
  'org_orientation_done',
  'contributor_promoted', // written by the SERVER at the Explorer→Contributor transition
  'contributor_promotion_ack',
  // Welcome screen (Skip 2026-07-07, migration 265)
  'welcome_screen_seen',
  'read_me_first_ack',
] as const;

export type MilestoneKey = typeof MILESTONE_KEYS[number];

// ── Archetype types ──────────────────────────────────────────

export const ARCHETYPE_KEYS = [
  'explorer',
  'contributor',
  'leader',
  'governor',
] as const;

export type ArchetypeKey = typeof ARCHETYPE_KEYS[number];

/**
 * Old slugs that may exist in user_onboarding_profiles rows or in
 * stale frontend state. `getArchetypeConfig()` maps these to the
 * current four-tier taxonomy so legacy data still resolves.
 */
const LEGACY_SLUG_MAP: Record<string, ArchetypeKey> = {
  // Original 7-archetype taxonomy
  expert: 'leader',
  youth: 'explorer',
  trainee: 'contributor',
  project_manager: 'leader',
  portfolio_observer: 'governor',
  system_admin: 'governor',
  advanced: 'governor',
};

// ── Feature flags ────────────────────────────────────────────

export type FeatureFlag =
  | 'discover_globe'
  | 'community_browse'
  | 'project_browse'
  | 'project_follow'
  | 'community_follow'
  | 'messaging'
  | 'comment'
  | 'volunteer'
  | 'impact_radar'
  | 'mini_radar'
  | 'growth_roles'
  | 'invite_others'
  | 'project_create'
  | 'workspace_create'
  | 'approvals'
  | 'kpi_entry'
  | 'tv'
  | 'feed'
  | 'bookmarks'
  | 'profile_full'
  | 'template_create'
  | 'governor_review';

/**
 * Runtime catalog of every feature flag (the union above is compile-time only).
 * Used by the admin feature-override modal to render the full toggle list.
 * Keep in sync with the FeatureFlag union.
 */
export const ALL_FEATURE_FLAGS: FeatureFlag[] = [
  'discover_globe', 'community_browse', 'project_browse', 'project_follow', 'community_follow',
  'messaging', 'comment', 'volunteer', 'impact_radar', 'mini_radar', 'growth_roles',
  'invite_others', 'project_create', 'workspace_create', 'approvals', 'kpi_entry', 'tv',
  'feed', 'bookmarks', 'profile_full', 'template_create', 'governor_review',
];

export interface FeatureCatalogEntry {
  flag: FeatureFlag;
  label: string;
  group: string;
}

/**
 * Human labels + grouping for every feature flag, for the admin access modal.
 * Order here is the display order (grouped by the LeftNav tiers). Keep in sync
 * with the FeatureFlag union / ALL_FEATURE_FLAGS.
 */
export const FEATURE_CATALOG: FeatureCatalogEntry[] = [
  { flag: 'discover_globe', label: 'Discover globe', group: 'Explore' },
  { flag: 'community_browse', label: 'Browse communities', group: 'Explore' },
  { flag: 'project_browse', label: 'Browse projects', group: 'Explore' },
  { flag: 'community_follow', label: 'Follow communities', group: 'Explore' },
  { flag: 'project_follow', label: 'Follow projects', group: 'Explore' },
  { flag: 'feed', label: 'Feed', group: 'Explore' },
  { flag: 'tv', label: 'L4E.TV', group: 'Explore' },
  { flag: 'bookmarks', label: 'Bookmarks', group: 'Explore' },
  { flag: 'profile_full', label: 'Full profile', group: 'Explore' },
  { flag: 'messaging', label: 'Messaging', group: 'Contribute' },
  { flag: 'comment', label: 'Comment', group: 'Contribute' },
  { flag: 'volunteer', label: 'Volunteer for roles', group: 'Contribute' },
  { flag: 'kpi_entry', label: 'KPI data entry', group: 'Contribute' },
  { flag: 'impact_radar', label: 'Impact Signature (radar)', group: 'Contribute' },
  { flag: 'mini_radar', label: 'Mini radar', group: 'Contribute' },
  { flag: 'growth_roles', label: 'Growth role suggestions', group: 'Contribute' },
  { flag: 'project_create', label: 'Create projects', group: 'Lead' },
  { flag: 'workspace_create', label: 'Create workspaces', group: 'Lead' },
  { flag: 'approvals', label: 'Action Center / approvals', group: 'Lead' },
  { flag: 'invite_others', label: 'Invite others', group: 'Govern' },
  { flag: 'template_create', label: 'Create templates', group: 'Govern' },
  { flag: 'governor_review', label: 'Governance review', group: 'Govern' },
];

// ── Severity tiers (DEV-681 Phase 3) ────────────────────────

export type MilestoneSeverity = 'mandatory' | 'recommended' | 'optional';

// ── Progressive disclosure: milestone → features unlocked ────

export interface MilestoneUnlock {
  /** Milestone key that triggers the unlock. */
  milestone: MilestoneKey;
  /** Severity tier for Tasks tab sort order + badge count. */
  severity: MilestoneSeverity;
  /** Features unlocked when this milestone is achieved. */
  features: FeatureFlag[];
  /** Nudge text i18n key shown until milestone is achieved. */
  nudgeKey: string;
  /** Optional route the Tasks tab CTA navigates to. */
  ctaRoute?: string;
  /** Optional navigation state passed alongside ctaRoute (e.g.
   *  `{ initialTab: 'about' }` — the DEV-991 deep-link mechanism on
   *  UserProfilePage). */
  ctaState?: Record<string, unknown>;
  /** Optional i18n key for the CTA button label. */
  ctaLabelKey?: string;
}

// ── Archetype configuration ──────────────────────────────────

export interface ArchetypeConfig {
  key: ArchetypeKey;
  labelKey: string;
  descriptionKey: string;
  defaultLandingRoute: string;
  /** Features available immediately (no milestone required). */
  baselineFeatures: FeatureFlag[];
  /** Ordered milestone progression — features unlock sequentially. */
  milestoneProgression: MilestoneUnlock[];
  /** Onboarding wizard steps (i18n keys). */
  onboardingSteps: OnboardingStep[];
}

export interface OnboardingStep {
  key: string;
  titleKey: string;
  bodyKey: string;
  action?: string;
  actionRoute?: string;
}

// ═══════════════════════════════════════════════════════════════
// Explorer Archetype
// ═══════════════════════════════════════════════════════════════
// "Curious newcomer. Guided path through the platform."
// Tier 1 of the LeftNav progression. New users default here.
//
// Explorer→Contributor advancement requires the mandatory Identity &
// Trust checklist (DEV-725) plus baseline platform literacy.

export const EXPLORER_CONFIG: ArchetypeConfig = {
  key: 'explorer',
  labelKey: 'onboarding.archetypes.explorer.label',
  descriptionKey: 'onboarding.archetypes.explorer.description',
  defaultLandingRoute: '/discover',

  baselineFeatures: [
    'discover_globe',
    'community_browse',
    'project_browse',
    'profile_full',
    // Promoted from the (now-removed) first_login milestone — these
    // were only gated behind a no-op "you logged in" milestone, so
    // they belong in baseline. See removed-milestone comment below.
    'community_follow',
    'project_follow',
    // Voice-ladder ruling (Skip, 2026-07-07): the main Feed is a READ /
    // discovery surface — Explorers need it to find things to follow
    // (their own rung basic). Reading is entry (§7.5); VOICE stays
    // gated: composers render Contributor+ (org-scoped exception for
    // standards-acked explorer members) and the share endpoint enforces
    // the same rung check server-side (NewsController.Share).
    'feed',
  ],

  /*
    Removed pending feature ship (do not re-add until these land):
      - first_follow         (DEV-540 Follow/Unfollow)
      - first_comment        (comment-create not yet built)
      - first_cross_community (depends on DEV-540)
    See MILESTONE_KEYS header comment for the full list.
  */
  milestoneProgression: [
    {
      milestone: 'enable_2fa',
      severity: 'mandatory',
      features: [],
      nudgeKey: 'onboarding.nudges.explorer.enable2fa',
      // No ctaRoute — TasksTab special-cases this milestone and opens
      // AccountSettings → Privacy & Security tab in-place via
      // useAccountSettings().openToTab('privacy'). Keeping the user
      // in-context preserves their flow.
      ctaLabelKey: 'onboarding.cta.enable2fa',
    },
    {
      milestone: 'complete_profile',
      severity: 'mandatory',
      features: [],
      nudgeKey: 'onboarding.nudges.explorer.completeProfile',
      ctaRoute: '/profile',
      // Photo + short bio live on About — don't strand the user on the
      // default Impact tab (Skip, 2026-07-08).
      ctaState: { initialTab: 'about' },
      ctaLabelKey: 'onboarding.cta.completeProfile',
    },
    {
      milestone: 'accept_eula',
      severity: 'mandatory',
      features: [],
      nudgeKey: 'onboarding.nudges.explorer.acceptEula',
      ctaRoute: '/profile',
      ctaLabelKey: 'onboarding.cta.acceptEula',
    },
    /*
      `first_login` was previously listed here as a recommended
      milestone that auto-completes the moment the user logs in. Skip
      flagged this as nonsensical — any user looking at the Tasks tab
      has, by definition, already logged in at least once. The
      milestone added no value (it auto-cleared on first render) and
      cluttered the worklist. The slug stays reserved in
      MILESTONE_KEYS in case we want to repurpose it for a "welcome
      back" nudge during a returning-user campaign someday.

      The features it would have unlocked (community_follow,
      project_follow) move to the baseline above, where they were
      effectively granted anyway since first_login was an instant pass.
    */
  ],

  onboardingSteps: [
    {
      key: 'welcome',
      titleKey: 'onboarding.steps.explorer.welcome.title',
      bodyKey: 'onboarding.steps.explorer.welcome.body',
    },
    {
      key: 'explore_community',
      titleKey: 'onboarding.steps.explorer.exploreCommunity.title',
      bodyKey: 'onboarding.steps.explorer.exploreCommunity.body',
      action: 'browse_community',
      actionRoute: '/communities',
    },
    {
      key: 'discover_projects',
      titleKey: 'onboarding.steps.explorer.discoverProjects.title',
      bodyKey: 'onboarding.steps.explorer.discoverProjects.body',
      action: 'browse_projects',
      actionRoute: '/projects',
    },
    {
      key: 'ready',
      titleKey: 'onboarding.steps.explorer.ready.title',
      bodyKey: 'onboarding.steps.explorer.ready.body',
    },
  ],
};

// ═══════════════════════════════════════════════════════════════
// Contributor Archetype
// ═══════════════════════════════════════════════════════════════
// "Active participant. Contributing to projects beyond their own."
// Tier 2. Earned via Explorer milestones; OR granted directly via
// invitation when an org adds someone they already trust.

export const CONTRIBUTOR_CONFIG: ArchetypeConfig = {
  key: 'contributor',
  labelKey: 'onboarding.archetypes.contributor.label',
  descriptionKey: 'onboarding.archetypes.contributor.description',
  defaultLandingRoute: '/',

  baselineFeatures: [
    ...EXPLORER_CONFIG.baselineFeatures,
    'community_follow',
    'project_follow',
    'messaging',
    'comment',
    'feed',
    'bookmarks',
    'tv',
    'volunteer',
    // §7.7 r4 / §7.16 (chunk B, DEV-1304): the Impact Signature unlocks AT
    // Contributor promotion — the reveal IS the promotion's identity beat.
    // These moved here from the retired `first_volunteer` milestone (the
    // vestigial mapping §7.16 cleans up): the radar + growth layer + KPI
    // entry are Contributor baseline, not gated on a first volunteer act.
    'impact_radar',
    'growth_roles',
    'kpi_entry',
  ],

  /*
    Removed pending feature ship (do not re-add until these land):
      - first_role_completed  (role completion lifecycle not yet built)
      - first_cross_community (depends on DEV-540 Follow/Unfollow)
    Removed by design (chunk B, §7.16):
      - first_volunteer — the vestigial impact_signature mapping; the
        reveal keys on promotion itself, never on a volunteer act.
  */
  milestoneProgression: [],

  onboardingSteps: [],
};

// ═══════════════════════════════════════════════════════════════
// Leader Archetype
// ═══════════════════════════════════════════════════════════════
// "Project owner / coordinator. Manages teams, sets targets, runs
// reporting cadences." Tier 3. Two paths in: Path A (institutional
// trust transfer from a Governor) or Path B (earned via mentor blessing
// after sustained Contributor activity). See DEV-725 for the model.

export const LEADER_CONFIG: ArchetypeConfig = {
  key: 'leader',
  labelKey: 'onboarding.archetypes.leader.label',
  descriptionKey: 'onboarding.archetypes.leader.description',
  defaultLandingRoute: '/',

  baselineFeatures: [
    ...CONTRIBUTOR_CONFIG.baselineFeatures,
    'impact_radar',
    'growth_roles',
    'kpi_entry',
    'mini_radar',
    'project_create',
    'approvals',
  ],

  /*
    Removed pending feature ship (do not re-add until these land):
      - earned_inviter_status (depends on DEV-672 trust scoring system)
  */
  milestoneProgression: [
    {
      milestone: 'first_project_created',
      severity: 'recommended',
      features: [],
      nudgeKey: 'onboarding.nudges.leader.createProject',
      ctaRoute: '/projects/new',
      ctaLabelKey: 'onboarding.cta.createProject',
    },
    {
      milestone: 'first_workspace_created',
      severity: 'recommended',
      features: ['workspace_create'],
      nudgeKey: 'onboarding.nudges.leader.createWorkspace',
    },
  ],

  onboardingSteps: [
    {
      key: 'welcome',
      titleKey: 'onboarding.steps.leader.welcome.title',
      bodyKey: 'onboarding.steps.leader.welcome.body',
    },
    {
      key: 'find_role',
      titleKey: 'onboarding.steps.leader.findRole.title',
      bodyKey: 'onboarding.steps.leader.findRole.body',
      action: 'browse_projects',
      actionRoute: '/projects',
    },
    {
      key: 'ready',
      titleKey: 'onboarding.steps.leader.ready.title',
      bodyKey: 'onboarding.steps.leader.ready.body',
    },
  ],
};

// ═══════════════════════════════════════════════════════════════
// Governor Archetype
// ═══════════════════════════════════════════════════════════════
// "Steward of the community. Authorizes leaders, reviews Best Practices,
// curates templates, sets quality standards." Tier 4 (top). Cannot be
// self-nominated; appointment-only or institutional trust transfer.

export const GOVERNOR_CONFIG: ArchetypeConfig = {
  key: 'governor',
  labelKey: 'onboarding.archetypes.governor.label',
  descriptionKey: 'onboarding.archetypes.governor.description',
  defaultLandingRoute: '/portfolio',

  baselineFeatures: [
    ...LEADER_CONFIG.baselineFeatures,
    'workspace_create',
    'invite_others',
    'template_create',
    'governor_review',
  ],

  // Governor milestones are intentionally minimal — by the time you
  // reach this tier, the platform has already given you everything.
  // The "next milestone" surface for Governors should focus on
  // mentorship and template curation activities, which are tracked
  // separately (out of scope for the Tasks tab MVP).
  milestoneProgression: [],

  onboardingSteps: [],
};

// ── Registry ─────────────────────────────────────────────────

export const ARCHETYPE_CONFIGS: Record<ArchetypeKey, ArchetypeConfig> = {
  explorer: EXPLORER_CONFIG,
  contributor: CONTRIBUTOR_CONFIG,
  leader: LEADER_CONFIG,
  governor: GOVERNOR_CONFIG,
};

/**
 * Resolve an archetype slug (current or legacy) to its config.
 * Falls back to EXPLORER_CONFIG for unknown slugs.
 *
 * Legacy slug migration is transparent — callers that pass `'expert'`
 * still receive the leader config. This means we don't need a DB
 * migration to update existing user_onboarding_profiles rows.
 */
export function getArchetypeConfig(key: string): ArchetypeConfig {
  if (!key) return EXPLORER_CONFIG;
  // Direct match against current taxonomy
  const direct = ARCHETYPE_CONFIGS[key as ArchetypeKey];
  if (direct) return direct;
  // Try legacy slug map
  const migrated = LEGACY_SLUG_MAP[key];
  if (migrated) return ARCHETYPE_CONFIGS[migrated];
  return EXPLORER_CONFIG;
}

/**
 * Compute the full set of unlocked features given a set of achieved
 * milestones. Features are additive — once unlocked, always unlocked.
 */
export function computeUnlockedFeatures(
  config: ArchetypeConfig,
  achievedMilestones: Set<MilestoneKey>,
): Set<FeatureFlag> {
  const features = new Set<FeatureFlag>(config.baselineFeatures);
  for (const unlock of config.milestoneProgression) {
    if (achievedMilestones.has(unlock.milestone)) {
      for (const f of unlock.features) {
        features.add(f);
      }
    }
  }
  return features;
}

/**
 * Find the next unachieved milestone in the archetype's progression.
 * Returns null if all milestones are achieved.
 *
 * Used by the wizard "what's next?" widget. The Tasks tab uses
 * `listIncompleteMilestones()` instead because it shows the full
 * outstanding list, not just the next one.
 */
export function getNextMilestone(
  config: ArchetypeConfig,
  achievedMilestones: Set<MilestoneKey>,
): MilestoneUnlock | null {
  for (const unlock of config.milestoneProgression) {
    if (!achievedMilestones.has(unlock.milestone)) {
      return unlock;
    }
  }
  return null;
}

/**
 * List ALL incomplete milestones in the archetype's progression,
 * sorted by severity (mandatory > recommended > optional) then by
 * order of declaration. Used by the Tasks tab to render the full
 * outstanding worklist.
 *
 * Mandatory milestones surface first because they block tier
 * advancement; recommended ones drive engagement quality; optional
 * ones round out the user's experience.
 */
export function listIncompleteMilestones(
  config: ArchetypeConfig,
  achievedMilestones: Set<MilestoneKey>,
): MilestoneUnlock[] {
  const incomplete = config.milestoneProgression.filter(
    (m) => !achievedMilestones.has(m.milestone),
  );
  // Stable sort by severity, preserving declaration order within each tier.
  const severityRank: Record<MilestoneSeverity, number> = {
    mandatory: 0,
    recommended: 1,
    optional: 2,
  };
  return [...incomplete].sort((a, b) => {
    const rankA = severityRank[a.severity];
    const rankB = severityRank[b.severity];
    if (rankA !== rankB) return rankA - rankB;
    // Within same severity, preserve order by re-finding declared index.
    return (
      config.milestoneProgression.indexOf(a) -
      config.milestoneProgression.indexOf(b)
    );
  });
}

/**
 * List COMPLETED milestones in the archetype's progression, in order
 * of declaration. Used by the Tasks tab's "Show completed" toggle.
 */
export function listCompletedMilestones(
  config: ArchetypeConfig,
  achievedMilestones: Set<MilestoneKey>,
): MilestoneUnlock[] {
  return config.milestoneProgression.filter((m) =>
    achievedMilestones.has(m.milestone),
  );
}
